import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Star, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ExitSurvey({ projectId, designerName }) {
  const [existing, setExisting] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/survey`);
        if (data && data.id) setExisting(data);
      } catch (_) {}
      setLoaded(true);
    })();
  }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Pick a rating from 1 to 5");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/survey`, { rating, comment });
      setExisting(data);
      toast.success("Thanks for the feedback!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) return null;

  if (existing && existing.id) {
    return (
      <div className="border border-[#262626] bg-[#0a0a0a] p-6" data-testid="survey-submitted">
        <div className="flex items-center gap-3 text-[#FF2D2D]">
          <CheckCircle size={20} weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.3em]">Feedback received</span>
        </div>
        <div className="font-display text-2xl mt-2">Thanks for rating us {existing.rating}/5.</div>
        {existing.comment && (
          <div className="text-sm text-[#a3a3a3] mt-3 italic">"{existing.comment}"</div>
        )}
      </div>
    );
  }

  const display = hover || rating;

  return (
    <form onSubmit={submit} className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-6" data-testid="survey-form">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Exit survey</div>
      <h3 className="font-display text-3xl mt-2">How was your experience?</h3>
      <div className="text-sm text-[#a3a3a3] mt-2">
        {designerName ? `Rate the work delivered by ${designerName}.` : "Help us track quality across the studio."}
      </div>

      <div className="flex items-center gap-2 mt-5" data-testid="survey-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-1"
            data-testid={`survey-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            <Star
              size={32}
              weight={display >= n ? "fill" : "regular"}
              className={display >= n ? "text-[#FF2D2D]" : "text-[#404040]"}
            />
          </button>
        ))}
        <span className="ml-3 text-sm text-[#a3a3a3] tabular-nums">{display ? `${display}/5` : "Pick a rating"}</span>
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What stood out? Anything we can improve? (optional)"
        rows={3}
        maxLength={2000}
        data-testid="survey-comment"
        className="mt-5 w-full bg-[#050505] border border-[#262626] px-4 py-3 text-sm focus:border-[#FF2D2D] outline-none resize-none"
      />

      <button
        type="submit"
        disabled={submitting || !rating}
        data-testid="survey-submit"
        className="btn-primary mt-4 px-5 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
      >
        {submitting ? "Sending…" : "Submit feedback"}
      </button>
    </form>
  );
}
